import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import VerifyIdentity from './pages/VerifyIdentity';
import VerifyRejected from './pages/VerifyRejected';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import NotFound from './pages/NotFound';

// Borrower
import BorrowerDashboard from './pages/borrower/BorrowerDashboard';
import LoanApplication from './pages/borrower/LoanApplication';
import LoanResults from './pages/borrower/LoanResults';
import LoanTracker from './pages/borrower/LoanTracker';
import CapitalPassport from './pages/borrower/CapitalPassport';

// Investor
import InvestorDashboard from './pages/investor/InvestorDashboard';
import Marketplace from './pages/investor/Marketplace';
import LoanDetail from './pages/investor/LoanDetail';
import SecondaryMarket from './pages/investor/SecondaryMarket';
import YieldHistory from './pages/investor/YieldHistory';
import Suitability from './pages/investor/Suitability';

// Admin
import AdminDashboard from './pages/admin/AdminDashboard';
import ApplicationsQueue from './pages/admin/ApplicationsQueue';
import LoanReview from './pages/admin/LoanReview';
import Analytics from './pages/admin/Analytics';
import AllLoans from './pages/admin/AllLoans';
import AdminUsers from './pages/admin/AdminUsers';

function ProtectedRoute({ children, roles, requireKyc = true, requireSuitability = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin w-10 h-10 border-3 border-purple-500 border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" />;

  // KYC gate: admins bypass, others must be verified
  if (requireKyc && user.role !== 'admin') {
    const kycStatus = user.kyc_status || 'pending';
    if (kycStatus === 'rejected') return <Navigate to="/verify-rejected" />;
    if (kycStatus !== 'verified') return <Navigate to="/verify-identity" />;
  }

  // Suitability gate: investors must complete the questionnaire before marketplace access
  if (requireSuitability && user.role === 'investor' && !user.suitability_completed) {
    return <Navigate to="/investor/suitability" replace />;
  }

  return <Layout>{children}</Layout>;
}

function getDashboardPath(user) {
  if (!user) return '/';
  return user.role === 'admin' ? '/admin' : user.role === 'investor' ? '/investor' : '/borrower';
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin w-10 h-10 border-3 border-purple-500 border-t-transparent rounded-full" /></div>;

  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={user ? <Navigate to={getDashboardPath(user)} /> : <Landing />} />
      <Route path="/login" element={user ? <Navigate to={getDashboardPath(user)} /> : <Login />} />
      <Route path="/signup" element={user ? <Navigate to={getDashboardPath(user)} /> : <Signup />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />

      {/* KYC */}
      <Route path="/verify-identity" element={user ? <VerifyIdentity /> : <Navigate to="/login" />} />
      <Route path="/verify-rejected" element={user ? <VerifyRejected /> : <Navigate to="/login" />} />

      {/* Borrower */}
      <Route path="/borrower" element={<ProtectedRoute roles={['borrower']}><BorrowerDashboard /></ProtectedRoute>} />
      <Route path="/borrower/apply" element={<ProtectedRoute roles={['borrower']}><LoanApplication /></ProtectedRoute>} />
      <Route path="/borrower/results" element={<ProtectedRoute roles={['borrower']}><LoanResults /></ProtectedRoute>} />
      <Route path="/borrower/loans" element={<ProtectedRoute roles={['borrower']}><BorrowerDashboard /></ProtectedRoute>} />
      <Route path="/borrower/loans/:loanId" element={<ProtectedRoute roles={['borrower']}><LoanTracker /></ProtectedRoute>} />
      <Route path="/borrower/passport" element={<ProtectedRoute roles={['borrower']}><CapitalPassport /></ProtectedRoute>} />

      {/* Investor */}
      <Route path="/investor" element={<ProtectedRoute roles={['investor']}><InvestorDashboard /></ProtectedRoute>} />
      <Route path="/investor/suitability" element={<ProtectedRoute roles={['investor']}><Suitability /></ProtectedRoute>} />
      <Route path="/investor/marketplace" element={<ProtectedRoute roles={['investor']} requireSuitability><Marketplace /></ProtectedRoute>} />
      <Route path="/investor/marketplace/:loanId" element={<ProtectedRoute roles={['investor']} requireSuitability><LoanDetail /></ProtectedRoute>} />
      <Route path="/investor/secondary" element={<ProtectedRoute roles={['investor']} requireSuitability><SecondaryMarket /></ProtectedRoute>} />
      <Route path="/investor/yield" element={<ProtectedRoute roles={['investor']}><YieldHistory /></ProtectedRoute>} />

      {/* Admin */}
      <Route path="/admin" element={<ProtectedRoute roles={['admin']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/applications" element={<ProtectedRoute roles={['admin']}><ApplicationsQueue /></ProtectedRoute>} />
      <Route path="/admin/applications/:loanId" element={<ProtectedRoute roles={['admin']}><LoanReview /></ProtectedRoute>} />
      <Route path="/admin/loans" element={<ProtectedRoute roles={['admin']}><AllLoans /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute roles={['admin']}><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute roles={['admin']}><Analytics /></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
